import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { AutoSizer, MultiGrid } from 'react-virtualized';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import Checkbox from '@mui/material/Checkbox';
import classNames from 'classnames';
import { fetchProviderData } from '@apps/shared/src/providerSearchAdmin/actions';
import { RootState } from '../store';

const useVirtualizedTableStyles = makeStyles({
  table: {
    '& .ReactVirtualized__Table__headerColumn': {
      margin: '0 !important',
    },
    '& .ReactVirtualized__Table__headerRow': {
      paddingLeft: '2px',
    },
    '& .ReactVirtualized__Table__row': {
      marginTop: 0,
      borderLeft: '2px solid transparent',
      '&:hover': {
        borderLeft: '2px solid #366BED',
        backgroundColor: '#EFF3FE',
      },
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: 'green',
    },
  },
  contentWrapper: {
    '&::-webkit-scrollbar': {
      width: '10px',
      height: '10px',
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb ': {
      background: 'grey',
      borderRadius: '10px',
    },
  },
  tableContainer: {
    width: 'auto',
    margin: 0,
    padding: 0,
    border: '1px solid #E0E0E0',
    borderWidth: '1px 1px 0px 1px',
    borderRadius: '0.5rem 0.5rem 0 0',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', // Adding box-shadow
    '& div[role=row]': {
      display: 'flex',
    },
  },
  tableAlign: (props: { roleCount: number }) => ({
    height: 'calc(75vh - 70px)',
    borderRadius: '0.5rem 0.5rem 0 0',
    boxShadow: 'none',
  }),
  column: {
    margin: '0 !important',
  },
  selectBox: {
    height: '10%',
    width: 'auto',
    borderTop: '1px solid #ECECEC',
    backgroundColor: '#fff',
    border: '1px solid #E0E0E0',
    borderRadius: '0 0 .5rem .5rem',
    overflow: 'hidden',
    '& .MuiSelect-selectMenu': {
      backgroundColor: 'white !important',
      width: 'auto',
      '&:focus': {
        border: 'none',
      },
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: '.5rem',
    },
  },
});

const useMuiTableStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    borderBottom: 'none',
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    fontSize: '0.8rem',
    height: '2rem',
    lineHeight: '1rem',
    textTransform: 'none',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    '& .MuiCheckbox-colorSecondary.Mui-checked': {
      color: '#EF7125',
    },
  },
  stickyCell: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    backgroundColor: '#fff',
  },
});

export type Data = Record<string, React.ReactNode>;

export type ColumnData = {
  dataKey: string;
  label: string;
  isNumeric?: boolean;
  style?: React.CSSProperties;
  width: number;
};

export type CellProps = Pick<TableCellProps, 'cellData' | 'columnIndex'> & {
  columns: ColumnData[];
  rowHeight: number;
  isHeader: boolean;
};

export type HeaderCellProps = {
  label: string;
  columnIndex: number;
  headerHeight: number;
  columns: ColumnData[];
};

export type CellRenderer = ({ cellData, columns, rowHeight, isHeader }: CellProps) => JSX.Element;

export type HeaderCellRenderer = ({
  label,
  columnIndex,
  headerHeight,
  columns,
}: HeaderCellProps) => JSX.Element;

function MuiCell({ cellData, columnIndex, columns, rowHeight, isHeader }: CellProps): JSX.Element {
  const classes = useMuiTableStyles();
  const isNumberOrString = typeof cellData === 'number' || typeof cellData === 'string';
  const columnWidth = columns[columnIndex].width;
  const isFixedColumn = columnIndex < 2;

  const renderCellContent = () => {
    if (columns[columnIndex].dataKey === 'active' && !isHeader) {
      const isChecked = cellData === 'true';
      return <Checkbox checked={isChecked} />;
    }

    if (cellData === null) return 'NULL';

    return cellData;
  };

  return (
    <TableCell
      className={classNames(classes.cell, {
        [classes.stickyCell]: isFixedColumn,
      })}
      style={{
        maxWidth: columnWidth,
        justifyContent: isFixedColumn ? 'left' : 'center',
      }}
      height={rowHeight}
      component="div"
      variant={isHeader ? 'head' : 'body'}
      title={isNumberOrString ? `${cellData}` : ''}
      align={columns[columnIndex].isNumeric ? 'right' : 'left'}
    >
      {renderCellContent()}
    </TableCell>
  );
}

function HeaderCell({ label, columnIndex, headerHeight, columns }: HeaderCellProps): JSX.Element {
  const classes = useMuiTableStyles();
  const columnWidth = columns[columnIndex].width;
  const isFixedColumn = columnIndex < 2;

  return (
    <TableCell
      className={classNames(classes.header, {
        [classes.stickyCell]: isFixedColumn,
      })}
      style={{
        maxWidth: columnWidth,
        justifyContent: isFixedColumn ? 'left' : 'center',
        borderBottom: columnIndex === 0 ? 'none' : undefined,
      }}
      height={headerHeight}
      component="div"
      variant="head"
      align={columns[columnIndex].isNumeric ? 'right' : 'left'}
    >
      {label}
    </TableCell>
  );
}

type VirtualizedTableProps = {
  columns: ColumnData[];
  data: Data[];
  rowHeight?: number;
  headerHeight?: number;
  dataTransformer?: (data: Data) => Data;
  cellRenderer?: CellRenderer;
  roleCount: number;
  fetchProviderData: typeof fetchProviderData;
  pageNo: number;
  pageSize: number;
  totalPages: number;
  searchInput: string;
  sortBy: string;
  recommendation: string;
  entityType: string;
};

function VirtualizedTable({
  columns,
  rowHeight = 70,
  headerHeight = 70,
  data,
  dataTransformer,
  roleCount,
  cellRenderer = MuiCell,
  fetchProviderData,
  pageNo,
  pageSize,
  totalPages,
  searchInput,
  sortBy,
  recommendation,
  entityType,
}: VirtualizedTableProps): JSX.Element {
  const classes = useVirtualizedTableStyles({ roleCount });
  const [page, setPage] = useState(pageNo - 1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const fetchData = (newPage: number, newRowsPerPage: number) => {
    fetchProviderData(newPage + 1, newRowsPerPage, searchInput, sortBy, recommendation, entityType);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchData(0, newRowsPerPage);
  };

  const getCellData = (rowIndex: number, columnIndex: number) => {
    if (rowIndex === 0) return columns[columnIndex].label;

    const rowData = dataTransformer ? dataTransformer(data[rowIndex - 1]) : data[rowIndex - 1];

    return rowData[columns[columnIndex].dataKey];
  };

  const getRowHeight = ({ index }: { index: number }) => {
    if (index === 0) return headerHeight; // header row

    const rowData = data[index - 1];
    let baseHeight = rowHeight;

    const checkKeys = ['internalNotes', 'externalNotes', 'providerName'];

    checkKeys.forEach(key => {
      const value = rowData[key];
      if (value && typeof value === 'string') {
        const lineHeight = 20; // Approximate line height
        const charsPerLine = 50; // Approximate chars per line
        const lines = Math.ceil(value.length / charsPerLine);
        baseHeight = Math.max(baseHeight, lines * lineHeight);
      }
    });

    return baseHeight;
  };

  const totalCount = pageSize * totalPages;

  return (
    <>
      <div className={classes.tableContainer}>
        <Paper className={classes.tableAlign}>
          <AutoSizer>
            {({ height, width }) => (
              <MultiGrid
                className={classes.table}
                fixedColumnCount={2}
                fixedRowCount={1}
                enableFixedColumnScroll
                enableFixedRowScroll
                hideTopRightGridScrollbar
                hideBottomLeftGridScrollbar
                classNameBottomRightGrid={classes.contentWrapper}
                columnCount={columns.length}
                columnWidth={({ index }) => columns[index].width}
                height={height || 100}
                rowCount={data.length + 1}
                rowHeight={getRowHeight}
                width={width || 100}
                scrollToAlignment="end"
                cellRenderer={({ columnIndex, rowIndex, style }) => (
                  <div style={style}>
                    {rowIndex === 0 ? (
                      <HeaderCell
                        label={getCellData(rowIndex, columnIndex) as string}
                        columnIndex={columnIndex}
                        headerHeight={headerHeight}
                        columns={columns}
                      />
                    ) : (
                      <MuiCell
                        cellData={getCellData(rowIndex, columnIndex)}
                        columnIndex={columnIndex}
                        rowHeight={rowHeight}
                        columns={columns}
                        isHeader={rowIndex === 0}
                      />
                    )}
                  </div>
                )}
              />
            )}
          </AutoSizer>
        </Paper>
      </div>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className={classes.selectBox}
      />
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  pageNo: state.providerSearchAdmin.pageNo,
  pageSize: state.providerSearchAdmin.pageSize,
  totalPages: state.providerSearchAdmin.totalPages,
  searchInput: state.providerSearchAdmin.searchInput,
  sortBy: state.providerSearchAdmin.sortBy,
  recommendation: state.providerSearchAdmin.recommendation,
  entityType: state.providerSearchAdmin.entityType,
});

const mapDispatchToProps = {
  fetchProviderData,
};

export default connect(mapStateToProps, mapDispatchToProps)(VirtualizedTable);
